import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Drawer, IconButton, Switch, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import type { ICampaign } from '../../../redux/buzzbridge/types';
import { updateOffsiteCampaign } from '../../../redux/buzzbridge/actions';
import { updatingCampaignSelector } from '../../../redux/buzzbridge/selectors';
import {
    campaignPlatformsSelector,
    activeInfluencerTiersSelector,
    buzzBridgeConfigSelector,
} from '../../../selectors/selectors';
import { filterEnabledSelectedPlatforms, isPlatformSelectable } from '../../../utils/ugcCampaignConfig';
import { areAllPlatformAutoLinksEnabled, formatDisabledAutoLinkPlatforms } from '../../../utils/brandSocialAutoLink';
import { DraggableFieldList } from '../../../components/Common/DraggableFieldList';
import { UgcInfluencerTierCards, UgcPlatformChips, UgcRewardTypeChips } from '../ugcCampaignTargetingUi';
import { CustomDatePicker } from '../../../components/DatePicker/CustomDatePicker';
import { INFLUENCER_TIERS, REWARD_OPTIONS, datePickerSx } from '../ugcCampaignFormHelpers';
import { showToastMessage } from '../../../utils/AppUtils';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER,
    BRAND_OUTLINE_VARIANT,
} from '../brandDashboardTokens';
import { FONT_ENUM } from '../../../types/global/fonts.types';

function FieldLabel({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            sx={{
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#fff',
                mb: 1.5,
            }}>
            {children}
        </Typography>
    );
}

interface Props {
    open: boolean;
    onClose: () => void;
    campaign: ICampaign;
}

export function CampaignTargetingPanel({ open, onClose, campaign }: Props) {
    const dispatch = useDispatch();
    const updating = useSelector(updatingCampaignSelector);
    const campaignPlatforms = useSelector(campaignPlatformsSelector);
    const apiTiers = useSelector(activeInfluencerTiersSelector);
    const tiers = apiTiers?.length > 0 ? apiTiers : INFLUENCER_TIERS;
    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);
    const canUseOauthCampaignModes = areAllPlatformAutoLinksEnabled(buzzBridgeConfig);
    const disabledAutoLinkLabel = formatDisabledAutoLinkPlatforms(buzzBridgeConfig);
    const showSyncUploadControls = canUseOauthCampaignModes || campaign.campaign_type === 'sync_upload';

    const isCommission = campaign.campaign_type === 'commission';
    const isDirectHireOrig = campaign.campaign_type === 'direct_hire';
    /** Published, paused, and closed campaigns have already gone live. */
    const targetingLocked =
        campaign.status === 'published' || campaign.status === 'paused' || campaign.status === 'closed';

    const [reward, setReward] = useState('');
    const [platforms, setPlatforms] = useState<string[]>([]);
    const [tier, setTier] = useState('');
    const [products, setProducts] = useState<string[]>([]);
    const [exclusiveRights, setExclusiveRights] = useState(false);
    const [physicalProduct, setPhysicalProduct] = useState(false);
    const [isDirectHire, setIsDirectHire] = useState(false);
    const [isSyncUpload, setIsSyncUpload] = useState(false);
    const [syncPublishDate, setSyncPublishDate] = useState('');

    useEffect(() => {
        if (!open) return;
        setReward(campaign.reward || '');
        setPlatforms(filterEnabledSelectedPlatforms((campaign.platforms || []) as string[], campaignPlatforms));
        setTier(campaign.influencer_tier || '');
        setProducts([...(campaign.products || [])]);
        setExclusiveRights(Boolean(campaign.exclusive_rights));
        setPhysicalProduct(Boolean(campaign.physical_product));
        setIsDirectHire(campaign.campaign_type === 'direct_hire');
        setIsSyncUpload(campaign.campaign_type === 'sync_upload');
        setSyncPublishDate(String((campaign as any).sync_publish_date || ''));
    }, [open, campaign, campaignPlatforms]);

    const togglePlatform = (p: string) => {
        setPlatforms(prev => (prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]));
    };

    const handleSave = () => {
        const arraysEqual = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);
        const body: Record<string, any> = {};

        if (!isCommission) {
            if (!targetingLocked && reward !== (campaign.reward || '')) body.reward = reward || undefined;

            if (!targetingLocked) {
                const origPlatforms = filterEnabledSelectedPlatforms(
                    (campaign.platforms || []) as string[],
                    campaignPlatforms,
                );
                const platformsForSubmit = isDirectHire
                    ? []
                    : filterEnabledSelectedPlatforms(platforms, campaignPlatforms);
                if (!arraysEqual([...platformsForSubmit].sort(), [...origPlatforms].sort()))
                    body.platforms = platformsForSubmit;
            }

            const cleanProducts = products.filter(Boolean);
            if (!arraysEqual(cleanProducts, campaign.products || [])) body.products = cleanProducts;

            if (exclusiveRights !== Boolean(campaign.exclusive_rights)) body.exclusive_rights = exclusiveRights;
            if (physicalProduct !== Boolean(campaign.physical_product)) body.physical_product = physicalProduct;

            if (!canUseOauthCampaignModes && isSyncUpload && campaign.campaign_type !== 'sync_upload') {
                showToastMessage(
                    `Sync upload requires Instagram, TikTok, and YouTube auto-link. Turn on auto-integration for ${
                        disabledAutoLinkLabel || 'all platforms'
                    } in global config.`,
                    'error',
                );
                return;
            }

            const origCampaignType = campaign.campaign_type || 'standard';
            const newCampaignType = isDirectHire ? 'direct_hire' : isSyncUpload ? 'sync_upload' : 'standard';
            if (newCampaignType !== origCampaignType) body.campaign_type = newCampaignType;

            if (isSyncUpload) {
                const nextSyncDate = String(syncPublishDate || '').trim();
                if (!nextSyncDate) {
                    showToastMessage('Pick a sync publish date for sync upload campaigns.', 'error');
                    return;
                }
                body.sync_publish_date = nextSyncDate;
            } else if (origCampaignType === 'sync_upload' && newCampaignType !== 'sync_upload') {
                body.sync_publish_date = null;
            }
        }

        if (!targetingLocked && tier !== (campaign.influencer_tier || '')) body.influencer_tier = tier || undefined;

        if (Object.keys(body).length === 0) {
            onClose();
            return;
        }
        dispatch(updateOffsiteCampaign({ campaignId: campaign._id, body }));
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100vw', sm: 540 },
                    bgcolor: '#111',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2.5,
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: '#fff' }}>Targeting</Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Influencer tier — shown for all types */}
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <FieldLabel>Influencer Tier</FieldLabel>
                    <UgcInfluencerTierCards
                        tiers={tiers}
                        selectedTierId={tier}
                        onSelect={setTier}
                        disabled={targetingLocked}
                    />
                    {targetingLocked && (
                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mt: 1.5 }}>
                            Cannot be changed after a campaign is live
                        </Typography>
                    )}
                </Box>

                {!isCommission && (
                    <>
                        {/* Reward Type */}
                        <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                            <FieldLabel>Reward Type</FieldLabel>
                            <UgcRewardTypeChips
                                value={reward}
                                options={REWARD_OPTIONS}
                                onChange={setReward}
                                disabled={targetingLocked}
                            />
                            {targetingLocked && (
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mt: 1.5 }}>
                                    Cannot be changed after a campaign is live
                                </Typography>
                            )}
                        </Box>

                        {/* Platforms */}
                        {!isDirectHire && (
                            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                                <FieldLabel>Platforms</FieldLabel>
                                <UgcPlatformChips
                                    platforms={campaignPlatforms}
                                    selectedIds={platforms}
                                    disabled={targetingLocked}
                                    onToggle={platformId => {
                                        if (targetingLocked) return;
                                        const platform = campaignPlatforms.find((p: any) => p.id === platformId);
                                        if (!platform || !isPlatformSelectable(platform)) return;
                                        togglePlatform(platformId);
                                    }}
                                />
                                {targetingLocked && (
                                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mt: 1.5 }}>
                                        Cannot be changed after a campaign is live
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {/* Products */}
                        <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                            <FieldLabel>Products / Services</FieldLabel>
                            <DraggableFieldList
                                items={products}
                                onItemsChange={setProducts}
                                placeholder="Add product or service..."
                            />
                        </Box>

                        {/* Exclusive rights */}
                        <Box
                            sx={{
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                p: 3,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}>
                            <Box>
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff' }}>
                                    Exclusive rights
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>
                                    Full commercial usage for 12 months
                                </Typography>
                            </Box>
                            <Switch
                                size="small"
                                checked={exclusiveRights}
                                onChange={(_, v) => setExclusiveRights(v)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: BRAND_LUXE_SECONDARY,
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: 'rgba(78,205,196,0.55)',
                                            opacity: 1,
                                        },
                                    },
                                    '& .MuiSwitch-track': { opacity: 1, backgroundColor: BRAND_OUTLINE_VARIANT },
                                }}
                            />
                        </Box>

                        {/* Physical product */}
                        <Box
                            sx={{
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                p: 3,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}>
                            <Box>
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff' }}>
                                    Physical product
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>
                                    Ship or deliver a physical item to creators
                                </Typography>
                            </Box>
                            <Switch
                                size="small"
                                checked={physicalProduct}
                                onChange={(_, v) => setPhysicalProduct(v)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: BRAND_LUXE_PRIMARY,
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: 'rgba(255,201,101,0.5)',
                                            opacity: 1,
                                        },
                                    },
                                    '& .MuiSwitch-track': { opacity: 1, backgroundColor: BRAND_OUTLINE_VARIANT },
                                }}
                            />
                        </Box>

                        {/* Direct hire — locked once published */}
                        <Box
                            sx={{
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                p: 3,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                opacity: campaign.status === 'published' ? 0.5 : 1,
                            }}>
                            <Box>
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff' }}>
                                    Direct hire
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>
                                    {campaign.status === 'published'
                                        ? 'Cannot be changed after a campaign is published'
                                        : 'Creators are hired directly, no live URL submission required'}
                                </Typography>
                            </Box>
                            <Switch
                                size="small"
                                checked={isDirectHire}
                                disabled={campaign.status === 'published'}
                                onChange={(_, v) => {
                                    setIsDirectHire(v);
                                    if (v) setIsSyncUpload(false);
                                }}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: BRAND_LUXE_SECONDARY,
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: 'rgba(0,108,80,0.5)',
                                            opacity: 1,
                                        },
                                    },
                                    '& .MuiSwitch-track': { opacity: 1, backgroundColor: BRAND_OUTLINE_VARIANT },
                                }}
                            />
                        </Box>

                        {/* Sync upload */}
                        {showSyncUploadControls && !isDirectHire && (
                            <Box
                                sx={{
                                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                    p: 3,
                                    borderRadius: 2,
                                    opacity: campaign.status === 'published' ? 0.5 : 1,
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                    }}>
                                    <Box>
                                        <Typography
                                            sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff' }}>
                                            Sync upload
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>
                                            All accepted creators publish simultaneously on the same date
                                        </Typography>
                                    </Box>
                                    <Switch
                                        size="small"
                                        checked={isSyncUpload}
                                        disabled={campaign.status === 'published'}
                                        onChange={(_, v) => setIsSyncUpload(v)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: BRAND_LUXE_PRIMARY,
                                                '& + .MuiSwitch-track': {
                                                    backgroundColor: 'rgba(255,201,101,0.4)',
                                                    opacity: 1,
                                                },
                                            },
                                            '& .MuiSwitch-track': {
                                                opacity: 1,
                                                backgroundColor: BRAND_OUTLINE_VARIANT,
                                            },
                                        }}
                                    />
                                </Box>
                                {isSyncUpload && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 12,
                                                color: 'rgba(255,255,255,0.5)',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.08em',
                                                mb: 1,
                                            }}>
                                            Publish date
                                        </Typography>
                                        <CustomDatePicker
                                            value={syncPublishDate || undefined}
                                            onChange={val => setSyncPublishDate(val || '')}
                                            minDate={new Date(Date.now() + 86400000).toISOString()}
                                            withTime={false}
                                            withTimezone={false}
                                            disabled={campaign.status === 'published'}
                                            sx={datePickerSx}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Not-available notice if OAuth missing */}
                        {!canUseOauthCampaignModes && !isDirectHireOrig && campaign.campaign_type !== 'sync_upload' && (
                            <Box
                                sx={{
                                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                    p: 3,
                                    borderRadius: 2,
                                    border: '1px solid rgba(255,201,101,0.22)',
                                }}>
                                <Typography
                                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff', mb: 0.5 }}>
                                    Sync upload unavailable
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55 }}>
                                    Instagram, TikTok, and YouTube auto-integration must all be enabled in global
                                    config. Currently off: {disabledAutoLinkLabel || 'one or more platforms'}.
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    bgcolor: `${BRAND_SURFACE_CONTAINER}80`,
                    display: 'flex',
                    gap: 2,
                    flexDirection: 'row-reverse',
                }}>
                <Button
                    onClick={handleSave}
                    disabled={updating}
                    sx={{
                        px: 4,
                        py: 1.5,
                        bgcolor: BRAND_LUXE_PRIMARY,
                        color: '#000',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        borderRadius: 3,
                        '&:hover': { bgcolor: '#e6b500' },
                        '&.Mui-disabled': { bgcolor: 'rgba(255,201,101,0.3)', color: 'rgba(0,0,0,0.4)' },
                        minWidth: 100,
                    }}>
                    {updating ? <CircularProgress size={16} sx={{ color: '#000' }} /> : 'Save'}
                </Button>
                <Button
                    onClick={onClose}
                    sx={{
                        px: 3,
                        py: 1.5,
                        color: 'rgba(255,255,255,0.6)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.1)',
                    }}>
                    Cancel
                </Button>
            </Box>
        </Drawer>
    );
}
